import type { LaunchDarklyFlagKey } from 'common/launchDarklyFlags';
import { type LDFlagValue, useFlags } from 'launchdarkly-react-client-sdk';

type FlagConfig = {
  flagKey: LaunchDarklyFlagKey;
  defaultValue: LDFlagValue;
};

/**
 * Reads one or more LaunchDarkly feature flags.
 *
 * Steps to add a new flag:
 * 1. Create the flag in the LaunchDarkly dashboard (kebab-case key).
 * 2. Add the key to `LAUNCH_DARKLY_FLAGS` in `src/common/launchDarklyFlags.ts`.
 * 3. Call this hook inside a React component:
 *
 * ```ts
 * import { LAUNCH_DARKLY_FLAGS } from 'common/launchDarklyFlags';
 * import useFeatureFlags from 'hooks/useFeatureFlags';
 *
 * const [isMyFeatureEnabled] = useFeatureFlag([
 *   { flagKey: LAUNCH_DARKLY_FLAGS['my-feature'], defaultValue: false },
 * ]);
 * ```
 *
 * Returns values in the same order as the input array.
 * Falls back to `defaultValue` when LaunchDarkly is unavailable or the flag is unset.
 */
const useFeatureFlags = (flagConfigs: FlagConfig[]): LDFlagValue[] => {
  const flags = useFlags();

  return flagConfigs.map(({ flagKey, defaultValue }) => {
    const value = flags?.[flagKey];
    return value ?? defaultValue;
  });
};

export default useFeatureFlags;
